import { Component, OnInit } from '@angular/core';
import { FormGroupToObject } from '../../pipes/form-group-to-object.pipe';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProductService } from '../../services/product.service';
import { Product, ProductPartner, ProductVariants, SuperProduct } from '../../models/product.schema';
import { PartnerService } from '../../services/partner.service';
import { AuthStatusService } from '../../services/auth-status.service';
import { plainToClass } from 'class-transformer';
import { ProductCategory } from 'src/app/models/product-category.schema';
import { ProductCategoryService } from 'src/app/services/product-category.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { AutoGeneratedSkuService } from 'src/app/helpers/autoGeneratedSku.service';
import { CommanUsesService } from 'src/app/services/comman-uses.service';
import { Partner } from 'src/app/models/partner.schema';
@Component({
  selector: 'app-duplicate-product',
  templateUrl: './duplicate-product.component.html',
  styleUrls: ['./duplicate-product.component.scss']
})
export class DuplicateProductComponent implements OnInit {

  public groups: string[];
  public partners: any=[];
  public readonly formValidationMsgs;
  public _productForm: any;
  public product: Product;
  public removeFile: boolean;
  public user_extra: {};
  public tmpBtnDisabled: boolean;
  productCategories: ProductCategory[] = [];
  allProductCategories: ProductCategory[] = [];
  imagePath: string;
  products: Product[] = [];
  selectedProductCategory: ProductCategory;
  productCategoryMetaFields: any[] = [];
  summerNoteConfig = {
    placeholder: '',
    tabsize: 2,
    height: '200px',
    uploadImagePath: '/api/upload',
    toolbar: [
      ['misc', ['codeview', 'undo', 'redo']],
      ['style', ['bold', 'italic', 'underline', 'clear', 'heading', 'style']],
      ['font', ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript', 'clear']],
      ['fontsize', ['fontname', 'fontsize', 'color']],
      ['para', ['style', 'ul', 'ol', 'paragraph', 'height']],
      ['insert', ['table', 'link', 'hr']]
    ],
    fontNames: ['Helvetica', 'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Roboto', 'Times']
  }
  oldProduct: Product;
  selectedPartner: Partner;
  imagePreviewUrl: string | ArrayBuffer | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authStatusService: AuthStatusService,
    private _snackBar: MatSnackBar,
    private productService: ProductService,
    private partnerService: PartnerService,
    private productCategoryService: ProductCategoryService,
    private spinnerService: SpinnerService,
    private fb: FormBuilder,
    private autoGeneratedSkuService: AutoGeneratedSkuService,
    private commanUsesService: CommanUsesService,
  ) {
    this.groups = this.authStatusService.getRoles().map((elem: string) => elem.toLowerCase());
    this.formValidationMsgs = Product.validationMessages;
    this.user_extra = this.authStatusService.getExtra();
  }

  async ngOnInit() {
    try {
      this.spinnerService.show();
      this.groups = this.authStatusService.getRoles().map((elem: string) => elem.toLowerCase());
      if(this.groups.includes('admin') || this.groups.includes('operation')){
        this.partners = (await this.partnerService.getAllActivePartnersForDelivery()).partners;
        this.products = (await this.productService.getAllActiveProductsForDelivery()).products;
      }
      this.productCategories = await this.productCategoryService.getAllProductCategories();
      await this.initialize();
      if (this.product) {
        this.products = this.products.filter(product => product.id !== this.product.id);
      }

      this._productForm.get('controlStock')?.valueChanges.subscribe((value) => {
        this.updateProductAvailabilityValidation(value);
      });

      this.updateProductAvailabilityValidation(this._productForm.get('controlStock')?.value);

      if (this.groups.includes('partner')) {
        try {
          const loggedInUserInfo = await this.authStatusService.getTokenInfo();
          const loggedInPartner = await this.partnerService.getOne(loggedInUserInfo['extraId']);
          this.selectedPartner = loggedInPartner;
          this.applyPartnerCategoryFilter(this.selectedPartner);
          this.updateAvailabilityValidators(this.selectedPartner.partnerHasWarehouse);
        } catch (error) {
          console.error('Error fetching partner data', error);
        }
      }
    }
    catch(error) {
      this.groups = [];
      this.commanUsesService.showSnackBar("Something went wrong on this page");
      console.log("ERROR", error);
      this.groups = [];
    } finally {
      this.spinnerService.hide();
    }
  }

  updateAvailabilityValidators(isWarehouse: boolean): void {
    const variantsArray = this._productForm.get('productVariants') as FormArray;

    variantsArray.controls.forEach((variantGroup: FormGroup) => {
      const variantAvailabilityControl = variantGroup.get('variantAvailability');

      if (isWarehouse) {
        variantAvailabilityControl?.setValidators([Validators.required, Validators.min(0)]);
      } else {
        variantAvailabilityControl?.setValidators([Validators.min(0)]);
      }

      variantAvailabilityControl?.updateValueAndValidity();
    });

    const productAvailabilityControl = this._productForm.get('productAvailability');
    if (isWarehouse) {
      productAvailabilityControl?.setValidators([Validators.required, Validators.min(0)]);
    } else {
      productAvailabilityControl?.setValidators([Validators.min(0)]);
    }

    productAvailabilityControl?.updateValueAndValidity();
  }

  updateProductAvailabilityValidation(controlStockValue: boolean) {
    const productAvailabilityControl = this._productForm.get('productAvailability');
    if (controlStockValue) {
      productAvailabilityControl?.setValidators([
        Validators.required,
        Validators.min(1),
      ]);
    } else {
      productAvailabilityControl?.clearValidators();
    }
    productAvailabilityControl?.updateValueAndValidity();
  }

  async initialize() {
    this.tmpBtnDisabled = false;
    this._productForm = new FormGroup(Product.validation());
    this.handleAlternateProductNameValidation();
    const productId: string = this.route.snapshot.paramMap.get('id');
    if (productId) {
      try {
        this.product = await this.productService.getOne(Number(productId));
        this.oldProduct = this.product;
      }
      catch {
        this.router.navigateByUrl("/404", { skipLocationChange: true });
      }
    }
    if (this.product){
      let sku = await this.autoGeneratedSkuService.autoGeneratedSku();
      if(sku){
        this._productForm.controls.sku.setValue(sku);
      }
      this.patch();
    }
    else {
      this.product = new Product();
      let sku = await this.autoGeneratedSkuService.autoGeneratedSku();
      if(sku){
        this._productForm.controls.sku.setValue(sku);
      }
      if(this.groups.includes('partner')) {
        this._productForm.controls.uniqueProduct.setValue(true);
      }
    }

    /*
    if(this.product.sku === '' || this.product.sku === undefined){
    }
    let sku = await this.autoGeneratedSkuService.autoGeneratedSku();
    if(sku){
      this._productForm.controls.sku.setValue(sku);
    }
    */
  }

  private handleAlternateProductNameValidation(): void {
    const isAlternateCtrl = this._productForm.get('isAlternateProductName');
    const alternateNameCtrl = this._productForm.get('alternateProductName');

    const updateValidation = (checked: boolean): void => {
      if (checked) {
        alternateNameCtrl?.setValidators([Validators.required]);
      } else {
        alternateNameCtrl?.clearValidators();
        alternateNameCtrl?.setValue('');
      }

      alternateNameCtrl?.updateValueAndValidity();
    };

    updateValidation(isAlternateCtrl?.value);

    isAlternateCtrl?.valueChanges.subscribe((checked: boolean) => {
      updateValidation(checked);
    });
  }

  async delete() {
    if (!confirm("Sei sicuro di voler cancellare questo prodotto?"))
      return;
    let product: { id: number | string, success: boolean } = await this.productService.delete(this.product.id);
    let message = "Cancellazione del prodotto fallita.";
    if (product.success)
      message = "Cancellazione del prodotto avvenuta con successo.";
    this._snackBar.open(message, 'Chiudi', {
      direction: "ltr",
      duration: 2000,
      horizontalPosition: "center",
      politeness: "assertive",
      verticalPosition: "top"
    }).afterDismissed().subscribe(result => {
      if (product.success)
        this.router.navigate(['/prodotti']);
    });
  }

  patch() {
    this.removeFile = false;
    [
      'name', 
      'description', 
      'price', 
      // 'sku',
      'quantity', 
      'unEditable', 
      'uniqueProduct', 
      'visibleToOtherPartners', 
      'productAvailability', 
      'adminApproval', 
      'inStock', 
      'isSuperProduct',
      'ggDispMin', 
      'productStatus', 
      'isDeluxyProduct', 
      'isCakeProduct', 
      'isFlowersProduct', 
      'isBusinessProduct',
      'shopifyPrice',
      'productAdvantageDesc',
      'cakeDescription',
      'flowersDescription',
      'deluxyDotComDescription',
      'deluxyExperienceDescription',
      'deluxyDescription',
      'businessDescription',
      'productHasVariants',
      'productOptionTitle',
      'notPhysical',
      'isSuperProvinceProduct',
      'controlStock',
      'isAlternateProductName',
      'alternateProductName',
    ].forEach(param => {
      this._productForm.controls[param].setValue(this.product[param]);
    });

    if (this.product['partner'])
      this._productForm.controls.partner.setValue(this.product['partner'].id);
    if (this.product.category)
      this._productForm.controls.category.setValue(this.product.category.join('; '));
    if (this.product.stock)
      this._productForm.controls.stock.setValue(this.product.stock.join('; '));
    if (this.product.line)
      this._productForm.controls.line.setValue(this.product.line.join('; '));
    if (this.product.productCategory){
      this._productForm.controls.productCategory.setValue(this.product?.productCategory?.id);
      this.selectedProductCategory = this.productCategories.find(c => c.id === this.product?.productCategory?.id);
      if(this.selectedProductCategory.categoryMetaFields){
        this.patchProductCategoryMetaFields(this.product.productCategoryMetaFields);
      }
    }
    if(this.product.image) {
      this.imagePath = this.product.image;
      this.imagePreviewUrl = this.product.image;
    }
    if(this.product?.productPartners?.length > 0){
      this.patchProductsPartner();
    }

    if(this.product?.superProducts?.length > 0){
      this.patchSuperProducts();
    }

    if(this.product?.productVariants?.length > 0){
      this.patchProductVariants(this.product.productVariants);
    }
    if(this.product['partner'] && this.product['partner'].id) {
      this.selectedPartner = this.partners.find((p) => p.id === Number(this.product['partner'].id));
    }
  } 

  patchProductsPartner(){
    const control = <FormArray>this._productForm.get('productPartners');
    control.clear();
    if (!this.product.productPartners || Array.isArray(this.product.productPartners) && this.product.productPartners.length == 0){
      this.product.productPartners = [new ProductPartner()];
    }
    this.product.productPartners.forEach((f, index) => {
      control.push(this.patchValues(f, index));
    });
  }

  patchValues(productPartners: ProductPartner, index: number) {
    let productPartner = Product.productPartnerFormGroup();
    productPartner.controls.partner.setValue(
      { 
        id: productPartners?.partner?.id, 
      }
    );
        
    return productPartner;
  }

  patchProductCategoryMetaFields(productCategoryMetaFields: any) {
    let metaFields: any[] = productCategoryMetaFields ? JSON.parse(productCategoryMetaFields) : [];
    this.populateMetaFields(metaFields);
  }

  errorMessage(createType, error) {
    let message = `Errore ${createType ? 'nella creazione' : "nell'aggiornamento"} del prodotto`;
    if (error.error && error.error.message === 'DUPLICATE.PRODUCT')
      message = `Errore: esiste già un prodotto con questo nome!`;
    if (error.error && error.error.message === 'DUPLICATE.PRODUCT_SKU')
      message = `Errore: esiste già un prodotto con questo sku!`;
    if(error.error && error.error.message === 'PRODUCT.ONLY_EDITABLE_ADMINISTRATION_SIDE'){
      message = `PRODOTTO SOLO LATO AMMINISTRAZIONE MODIFICABILE`
    }
    this._snackBar.open(message,
      'Chiudi',
      {
        direction: 'ltr',
        duration: 2000,
        horizontalPosition: 'center',
        politeness: 'assertive',
        verticalPosition: 'top',
      }
    );
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    if (!file.type.startsWith('image/')) {
      this._snackBar.open('Please select a valid image file.', 'Chiudi', {
        direction: 'ltr',
        duration: 2000,
        horizontalPosition: 'center',
        politeness: 'assertive',
        verticalPosition: 'top',
      });

      input.value = '';
      return;
    }

    this.removeFile = false;

    this._productForm.get('image')?.setValue(file);
    this._productForm.get('image')?.markAsDirty();
    this._productForm.get('image')?.updateValueAndValidity();

    const reader = new FileReader();

    reader.onload = () => {
      this.imagePreviewUrl = reader.result;
      this.imagePath = reader.result as string;
    };

    reader.readAsDataURL(file);
  }

  uploadImage(productId: number) {
    const imageFile = this._productForm.get('image')?.value;

    return this.productService.updateProductPhoto(
      productId,
      imageFile
    );
  }

  deleteFile(): void {
    this.removeFile = true;
    this.imagePreviewUrl = null;
    this.imagePath = null;

    this._productForm.get('image')?.setValue(null);
    this._productForm.get('image')?.markAsDirty();
    this._productForm.get('image')?.updateValueAndValidity();
  }

  openImage(event) {
    let w = window.open("");
    w.document.write(event.target.outerHTML);
  }

  addPartner() {
    const control = <FormArray>this._productForm.get('productPartners');
    control.push(Product.productPartnerFormGroup());
  }

  removePartner(index: number) {
    const control = <FormArray>this._productForm.get('productPartners');
    if (control.length === 1) {
      return;
    }
    control.removeAt(index);
  }

  isPartnerOptionDisabled(item: any): boolean {
    let hasEmptyId = false;
    for (const partners of this._productForm.value.productPartners) {
      if (partners.partner.id === item.id) {
        hasEmptyId = true;
      }
    }
    return hasEmptyId;
  }
  onDuplicate(){
    this.tmpBtnDisabled = true;
    let product: Product = new FormGroupToObject().transform(this._productForm);
    
    const selectedImage = this._productForm.get('image')?.value;
    const hasNewImage = selectedImage instanceof File;
    const hasOriginalImage = !!this.oldProduct?.image;

    if(this.groups.includes('partner')){
      product['partner'] = this.user_extra['id'];
    }

    delete product.image;

    product = plainToClass(Product, product);

    if(this.groups.includes('partner')){
      product.partner = this.user_extra['id'];
      delete product?.productPartners;
    }

    if(product.productCategoryMetaFields){
      product.productCategoryMetaFields = product.productCategoryMetaFields ? JSON.stringify(product.productCategoryMetaFields) : null;
    }

    if(this.groups.includes('admin') || this.groups.includes('operation')){
      if(product?.productPartners[0]?.partner?.id === null || product?.productPartners[0]?.partner?.id === undefined || product?.productPartners[0]?.partner?.id === ''){
        delete product?.productPartners;
      }
    }

    if(this.groups.includes('admin') || this.groups.includes('operation')){
      if(product?.superProducts[0]?.product?.id === null || product?.superProducts[0]?.product?.id === undefined || product?.superProducts[0]?.product?.id === ''){
        delete product?.superProducts;
      }
    }
    
    let createType: boolean = true;

    // console.log("PRODUCT_INFO", product); return;

    this.productService.create(product)
      .then(async (data: Product) => {  
        let allGood = true;
        this.product = await this.productService.getOne(data.id);

        try {
          /**
           * Priority:
           * 1. If user selected a new image, upload the new image.
           * 2. Else if original product has image and user did not remove it, copy old image.
           * 3. Else keep duplicated product without image.
           */
          if (hasNewImage) {
            this.product = await this.uploadImage(data.id);
          } else if (hasOriginalImage && !this.removeFile) {
            this.product = await this.productService.uploadDuplicateImage(
              this.oldProduct.id,
              data.id
            );
          }
        } catch (err) {
          this.errorMessage(createType, err);
          allGood = false;
        }

        if (allGood) {
          const message = `Prodotto duplicata con successo. Nuovo id creato : '${this.product.id}'`;

          this._snackBar.open(message, 'Chiudi', {
            direction: 'ltr',
            duration: 2000,
            horizontalPosition: 'center',
            politeness: 'assertive',
            verticalPosition: 'top',
          }).afterDismissed().subscribe(() => {
            this.router.navigate(['/prodotti', this.product.id]);
          });

          this.tmpBtnDisabled = false;
          this.patch();
        }
      })
      .catch((error) => {
        console.log("error", error);
        this.tmpBtnDisabled = false;
        this.errorMessage(createType, error);
      });
  }

  patchSuperProducts(){
    const control = <FormArray>this._productForm.get('superProducts');
    control.clear();
    if (!this.product.superProducts || Array.isArray(this.product.superProducts) && this.product.superProducts.length == 0){
      this.product.superProducts = [new SuperProduct()];
    }
    this.product.superProducts.forEach((f, index) => {
      control.push(this.patchSuperProductValues(f, index));
    });
  }

  patchSuperProductValues(superProducts: SuperProduct, index: number) {
    let superProduct = Product.superProductFormGroup();
    superProduct.controls.product.setValue(
      { 
        id: superProducts?.product?.id, 
      }
    );
        
    return superProduct;
  }

  addProduct() {
    const control = <FormArray>this._productForm.get('superProducts');
    control.push(Product.superProductFormGroup());
  }

  removeProduct(index: number) {
    const control = <FormArray>this._productForm.get('superProducts');
    if (control.length === 1) {
      return;
    }
    control.removeAt(index);
  }

  isProductOptionDisabled(item: any): boolean {
    let hasEmptyId = false;
    for (const products of this._productForm.value.superProducts) {
      if (products?.product?.id === item.id) {
        hasEmptyId = true;
      }
    }
    return hasEmptyId;
  }

  handlePartnerChange(partnerId: number){
    // if(partnerId){
    //   this.partners = this.partners.filter(p => p.id != partnerId);
    // }
    this.resetCategorySelection();
    this.selectedPartner = this.partners.find((p) => p.id == partnerId);
    this.applyPartnerCategoryFilter(this.selectedPartner);
    if(this.selectedPartner.partnerHasWarehouse) this.updateAvailabilityValidators(this.selectedPartner.partnerHasWarehouse);
  }

  async addProductVariant() {
    const control = this._productForm.get('productVariants') as FormArray;
    const newVariantGroup = new FormGroup(ProductVariants.validation());
    let productSku = this._productForm.get('sku').value;
    const index = control.length;
    const indexedSku = `${productSku}-${index + 1}`;
    control.push(newVariantGroup);
    newVariantGroup.get('variantSku')?.setValue(indexedSku);
    if(this.selectedPartner.partnerHasWarehouse) this.updateAvailabilityValidators(this.selectedPartner.partnerHasWarehouse);
  }

  removeProductVariant(index: number) {
    const control = this._productForm.get('productVariants') as FormArray;
    control.removeAt(index);
    if (control.length > 1) {
    }
  }

  handleProductCategoryChange(categoryId: any){
    this.selectedProductCategory = this.productCategories.find(c => c.id == categoryId);
    this.populateMetaFields();
  }

  get metaFields(): FormArray {
    return this._productForm.get('productCategoryMetaFields') as FormArray;
  }

  populateMetaFields(metaFields?: any[]) {
    this.metaFields.clear();
    this.productCategoryMetaFields = this.selectedProductCategory?.categoryMetaFields ? JSON.parse(this.selectedProductCategory.categoryMetaFields) : [];
    this.formValidationMsgs['productCategoryMetaFields'] = [];
    this.productCategoryMetaFields.forEach((field: any, index: number) => {
      const value = metaFields && metaFields[index] ? metaFields[index] : '';
      const control = this.fb.control(value, ((field.type === "1" || field.type === "3") && this.groups.includes('admin') ? Validators.required : []));
      this.metaFields.push(control);
      this.formValidationMsgs[field.fieldName] = [{ type: 'required', message: `${field.fieldName} is Obbligatorio.` }];
    });
  }

  patchProductVariants(productVariantsData: ProductVariants[]){
    const formArray = this.productVariantsFields;
    formArray.clear();
    productVariantsData.forEach((field, index) => {
      let productSku = this._productForm.get('sku').value;
      const indexedSku = `${productSku}-${index + 1}`;
      formArray.push(new FormGroup({
        id: new FormControl(field.id),
        variantName: new FormControl(field.variantName),
        variantSku: new FormControl(indexedSku),
        variantPrice: new FormControl(field.variantPrice),
        variantShopifyPrice: new FormControl(field.variantShopifyPrice),
        variantGgDispMin: new FormControl(field.variantGgDispMin),
        variantInStock: new FormControl(field.variantInStock),
        controlVariantStock: new FormControl(field.controlVariantStock),
        variantAvailability: new FormControl(field.variantAvailability),
      }));
    });
  }

  get productVariantsFields(): FormArray {
    return this._productForm.get('productVariants') as FormArray;
  }

  handleShopCheckbox(event: any, shopName: string) {
    if (!event.target.checked) return;
  
    const keyPointsMap = {
      deluxy: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li><b>Consegna</b>: in guanti bianchi, a casa o dove vuoi tu</li>`,
      cake: `
        <li><b>Personalizza</b>: scegli forma, gusti e topping</li>
        <li>Consegna dove vuoi tu nel mondo</li>`,
      flowers: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li>Consegna inclusa dove vuoi tu</li>`,
      business: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li><b>Consegna</b>: in guanti bianchi, a casa o dove vuoi tu</li>`,
      deluxyExperience: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li>Consegna inclusa dove vuoi tu</li>`,
      deluxyDotCom: `
        <li><b>Inclusi</b>: biglietto scritto a mano e confezione regalo</li>
        <li><b>Consegna</b>: in guanti bianchi, a casa o dove vuoi tu</li>`,
    };
  
    const key = shopName;
    if (!keyPointsMap[key]) return;
  
    let text = `
      <ul>
        <li><b>${this.selectedProductCategory.categoryName || ''}</b>: 
        ${this._productForm.get('productAdvantageDesc').value || ''}</li>
        ${keyPointsMap[key]}
      </ul>
      <h6>DESCRIZIONE</h6>
      <p>${this._productForm.get('description').value || ''}</p>
    `.replace(/\s+/g, ' ').trim();
  
    this.productCategoryMetaFields.forEach((metaField, i) => {
      text += `<h6>${metaField.fieldName}</h6><p>${this._productForm.get('productCategoryMetaFields').value[i]}</p>`;
    });
  
    this._productForm.get(`${key}Description`).setValue(text);
  }

  productHasVariant(event: any) {
    const checked: boolean = event.target.checked;
    const productOptionTitleControl = this._productForm.get('productOptionTitle');
  
    if (checked) {
      productOptionTitleControl?.setValidators([Validators.required]);
    } else {
      productOptionTitleControl?.clearValidators();
    }
  
    productOptionTitleControl?.updateValueAndValidity();
  }

  private applyPartnerCategoryFilter(partner?: Partner) {
    if (!partner) {
      this.productCategories = [...this.allProductCategories];
      return;
    }

    const ids: number[] =
      partner?.partnerSoldProductCategories
        ?.map((x: any) => x?.productCategory?.id)
        ?.filter((id: any) => typeof id === 'number') || [];

    const allowedIds = new Set<number>(ids);

    if (allowedIds.size === 0) {
      this.productCategories = [...this.allProductCategories];
      return;
    }

    this.productCategories = this.allProductCategories.filter((c) => allowedIds.has(c.id));

    const currentCategoryId = this._productForm?.get('productCategory')?.value;
    if (currentCategoryId && !allowedIds.has(Number(currentCategoryId))) {
      this._productForm.get('productCategory')?.setValue(null);
      this.selectedProductCategory = undefined;
      this.populateMetaFields();
    }
  }

  private resetCategorySelection(): void {
    this._productForm.get('productCategory')?.setValue(null);
    this.selectedProductCategory = undefined;
    this.productCategoryMetaFields = [];
    this.metaFields.clear();
  }

  getErrorMessages(): string[] {
    const errorMessages: string[] = [];

    /* ============================
      TOP-LEVEL PRODUCT CONTROLS
      ============================ */
    Object.keys(this._productForm.controls).forEach(key => {
      const control = this._productForm.get(key);
      const controlErrors = control?.errors;

      if (controlErrors && Product.validationMessages[key]) {
        Object.keys(controlErrors).forEach(errorKey => {
          const messageObj = Product.validationMessages[key]
            .find((msg: any) => msg.type === errorKey);

          if (messageObj && !errorMessages.includes(key)) {
            errorMessages.push(key);
          }
        });
      }
    });

    /* ============================
      PRODUCT VARIANTS (FormArray)
    ============================ */
    const variantsArray = this._productForm.get('productVariants') as FormArray;

    if (variantsArray && variantsArray.controls.length > 0) {
      variantsArray.controls.forEach(variantGroup => {

        const group = variantGroup as FormGroup;

        Object.keys(group.controls).forEach(controlKey => {
          const control = group.get(controlKey);
          const errors = control?.errors;

          if (
            errors &&
            Product.validationMessages.productVariants &&
            Product.validationMessages.productVariants[controlKey]
          ) {
            Object.keys(errors).forEach(errorKey => {
              const messageObj =
                Product.validationMessages.productVariants[controlKey]
                  .find((msg: any) => msg.type === errorKey);

              if (messageObj && !errorMessages.includes(controlKey)) {
                errorMessages.push(controlKey);
              }
            });
          }
        });
      });
    }

    /* =========================================
    PRODUCT CATEGORY META FIELDS (DYNAMIC)
    ========================================= */
    const metaFieldsArray = this._productForm.get('productCategoryMetaFields') as FormArray;

    if (metaFieldsArray && this.productCategoryMetaFields?.length) {
      metaFieldsArray.controls.forEach((control, index) => {
        if (control.errors) {
          const fieldName = this.productCategoryMetaFields[index]?.fieldName;

          if (
            fieldName &&
            this.formValidationMsgs[fieldName] &&
            !errorMessages.includes(fieldName)
          ) {
            errorMessages.push(fieldName);
          }
        }
      });
    }

    return errorMessages;
  }


  translateErrorMessage(error: string): string {
    const staticMessages: { [key: string]: string } = {
      name: 'NOME PRODOTTO',
      sku: 'SKU',
      productCategory: 'CATEGORIA',
      price: 'PREZZO',
      shopifyPrice: 'PREZZO SHOPIFY',
      quantity: 'QUANTITÀ',
      image: 'IMMAGINE',
      productAdvantageDesc: 'DESCRIZIONE VANTAGGI',
      productOptionTitle: 'TITOLO OPZIONI',
      productAvailability: 'INVENTARIO',
      alternateProductName: 'NOME ALTERNATIVO DEL PRODOTTO',

      // VARIANTS
      variantName: 'NOME VARIANTE',
      variantSku: 'SKU VARIANTE',
      variantPrice: 'PREZZO VARIANTE',
      variantShopifyPrice: 'PREZZO SHOPIFY VARIANTE',
      variantAvailability: 'INVENTARIO VARIANTE',
    };

    if (staticMessages[error]) {
      return staticMessages[error];
    }

    const dynamicLabel = this.productCategoryMetaFields
      ?.find((f: any) => f.fieldName === error)
      ?.fieldName;

    return dynamicLabel?.toUpperCase() || error;
  }
}
